import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  Grid, 
  CircularProgress,
  IconButton,
  Divider,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Card,
  CardContent,
  Avatar,
  Chip,
  Fade,
  Collapse
} from '@mui/material';
import { 
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  ViewColumn as ViewColumnIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Functions as FunctionsIcon,
} from '@mui/icons-material';
import { useForm, Controller, SubmitHandler, Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import PageHeader from '../components/common/PageHeader';
import FormulaEditor from './FormulaEditor';
import { TableService } from '../services/table.service';
import { ColumnType, ColumnFormula } from '../types/table.types';

interface TableFormData {
  name: string;
  description?: string;
}

const tableSchema = yup.object({
  name: yup.string().required('Название таблицы обязательно'),
  description: yup.string().optional(),
}).required();

interface ColumnFormData {
  name: string;
  type: ColumnType;
  description?: string;
  isRequired?: boolean;
  isUnique?: boolean;
  options?: any;
  formula?: ColumnFormula | null;
}

const columnSchema = yup.object({
  name: yup.string().required('Название колонки обязательно'),
  type: yup.mixed<ColumnType>().required('Тип колонки обязателен'),
  description: yup.string().optional(),
  isRequired: yup.boolean().optional(),
  isUnique: yup.boolean().optional(),
}).required();

const columnTypeIcons: Record<ColumnType, { icon: string; color: string }> = {
  [ColumnType.TEXT]: { icon: 'T', color: '#2196f3' },
  [ColumnType.NUMBER]: { icon: '#', color: '#4caf50' },
  [ColumnType.DATE]: { icon: '📅', color: '#ff9800' },
  [ColumnType.BOOLEAN]: { icon: '✓', color: '#9c27b0' },
  [ColumnType.DROPDOWN]: { icon: '▼', color: '#f44336' },
  [ColumnType.REFERENCE]: { icon: '→', color: '#00bcd4' },
};

export default function TableCreate() {
  const navigate = useNavigate();
  
  const [columns, setColumns] = useState<ColumnFormData[]>([]);
  const [showColumnForm, setShowColumnForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dropdownOptions, setDropdownOptions] = useState<string[]>([]);
  const [newOption, setNewOption] = useState('');
  const [columnFormula, setColumnFormula] = useState<ColumnFormula | null>(null);

  const { 
    control: tableControl, 
    handleSubmit: handleTableSubmit, 
    formState: { errors: tableErrors }
  } = useForm<TableFormData>({
    resolver: yupResolver(tableSchema) as Resolver<TableFormData>,
    defaultValues: {
      name: '',
      description: '',
    }
  });
  
  const { 
    control: columnControl, 
    handleSubmit: handleColumnSubmit, 
    formState: { errors: columnErrors },
    reset: resetColumnForm,
    watch: watchColumn,
  } = useForm<ColumnFormData>({
    resolver: yupResolver(columnSchema) as Resolver<ColumnFormData>,
    defaultValues: {
      name: '',
      type: ColumnType.TEXT,
      description: '',
      isRequired: false,
      isUnique: false,
    }
  });

  const watchColumnType = watchColumn('type');
  const watchColumnName = watchColumn('name');
  
  const handleAddColumn = (data: ColumnFormData) => {
    const columnData = { ...data };
    if (data.type === ColumnType.DROPDOWN) {
      columnData.options = { values: dropdownOptions };
    }
    columnData.formula = columnFormula;
    setColumns([...columns, columnData]);
    resetColumnForm();
    setDropdownOptions([]);
    setNewOption('');
    setColumnFormula(null);
    setShowColumnForm(false);
  };

  const handleAddOption = () => {
    const trimmed = newOption.trim();
    if (trimmed && !dropdownOptions.includes(trimmed)) {
      setDropdownOptions([...dropdownOptions, trimmed]);
      setNewOption('');
    }
  };

  const handleRemoveOption = (index: number) => {
    setDropdownOptions(dropdownOptions.filter((_, i) => i !== index));
  };
  
  const handleMoveColumnUp = (index: number) => {
    if (index <= 0) return;
    const newColumns = [...columns];
    const temp = newColumns[index - 1];
    newColumns[index - 1] = newColumns[index];
    newColumns[index] = temp;
    setColumns(newColumns);
  };
  
  const handleMoveColumnDown = (index: number) => {
    if (index >= columns.length - 1) return;
    const newColumns = [...columns];
    const temp = newColumns[index + 1];
    newColumns[index + 1] = newColumns[index];
    newColumns[index] = temp;
    setColumns(newColumns);
  };
  
  const handleRemoveColumn = (index: number) => {
    setColumns(columns.filter((_, i) => i !== index));
  };

  const handleCloseColumnForm = () => {
    setShowColumnForm(false);
    setDropdownOptions([]);
    setNewOption('');
    setColumnFormula(null);
  };
  
  const handleCancel = () => {
    navigate('/tables');
  };
  
  const onSubmitTable = async (data: TableFormData) => {
    if (columns.length === 0) {
      setError('Необходимо добавить хотя бы одну колонку');
      return;
    }
    
    try {
      setSubmitting(true);
      setError(null);
      
      const newTable = {
        ...data,
        columns: columns.map((column, index) => ({
          ...column,
          displayOrder: index,
          // Отправляем formula только если она задана
          formula: column.formula || undefined,
        })),
      };
      
      await TableService.createTable(newTable);
      
      setSuccess('Таблица успешно создана');
      setTimeout(() => {
        navigate('/tables');
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка создания таблицы');
    } finally {
      setSubmitting(false);
    }
  };

  // Колонки доступные как source для формул (все уже добавленные + текущая, если введено имя)
  const availableColumnsForFormula = columns.map(c => ({ name: c.name, type: c.type }));
  
  return (
    <Box>
      <PageHeader 
        title="Создание таблицы"
        subtitle="Определите структуру новой таблицы"
        breadcrumbs={[
          { title: 'Таблицы', href: '/tables' },
          { title: 'Создание' },
        ]}
      />
      
      <Collapse in={!!error}>
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      </Collapse>
      
      <Collapse in={!!success}>
        <Alert severity="success" icon={<CheckIcon />} sx={{ mb: 3, borderRadius: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Collapse>
      
      {/* Основная информация */}
      <Fade in timeout={600}>
        <Paper 
          sx={{ 
            p: 4, 
            mb: 3,
            borderRadius: 3,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Avatar sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)', color: '#667eea', width: 56, height: 56 }}>
              <ViewColumnIcon sx={{ fontSize: 30 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Основная информация
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Укажите название и описание таблицы
              </Typography>
            </Box>
          </Box>
          
          <Divider sx={{ mb: 3 }} />
          
          <form>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="name"
                  control={tableControl}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Название таблицы"
                      fullWidth
                      required
                      error={!!tableErrors.name}
                      helperText={tableErrors.name?.message}
                      disabled={submitting}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  )}
                />
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <Controller
                  name="description"
                  control={tableControl}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Описание"
                      fullWidth
                      multiline
                      rows={3}
                      error={!!tableErrors.description}
                      helperText={tableErrors.description?.message}
                      disabled={submitting}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Fade>
      
      {/* Колонки */}
      <Fade in timeout={800}>
        <Paper 
          sx={{ 
            p: 4, 
            mb: 3,
            borderRadius: 3,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #f093fb 0%, #f5576c 100%)',
            }
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(245, 87, 108, 0.1)', color: '#f5576c', width: 56, height: 56 }}>
                <ViewColumnIcon sx={{ fontSize: 30 }} />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Колонки
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Добавлено: {columns.length}
                </Typography>
              </Box>
            </Box>
            
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowColumnForm(true)}
              disabled={submitting || showColumnForm}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)',
                }
              }}
            >
              Добавить колонку
            </Button>
          </Box>
          
          <Divider sx={{ mb: 3 }} />
          
          <Collapse in={showColumnForm}>
            <Card 
              sx={{ 
                mb: 3, 
                bgcolor: 'rgba(245, 87, 108, 0.05)',
                border: '1px solid rgba(245, 87, 108, 0.2)',
                borderRadius: 2,
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Добавление колонки
                  </Typography>
                  <IconButton size="small" onClick={handleCloseColumnForm}>
                    <CloseIcon />
                  </IconButton>
                </Box>
                
                <form onSubmit={handleColumnSubmit(handleAddColumn as SubmitHandler<ColumnFormData>)}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Controller
                        name="name"
                        control={columnControl}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Название колонки"
                            fullWidth
                            required
                            error={!!columnErrors.name}
                            helperText={columnErrors.name?.message}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              }
                            }}
                          />
                        )}
                      />
                    </Grid>
                    
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Controller
                        name="type"
                        control={columnControl}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            select
                            label="Тип данных"
                            fullWidth
                            required
                            error={!!columnErrors.type}
                            helperText={columnErrors.type?.message}
                            onChange={(e) => {
                              field.onChange(e);
                              if (e.target.value !== ColumnType.DROPDOWN) {
                                setDropdownOptions([]);
                                setNewOption('');
                              }
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              }
                            }}
                          >
                            <MenuItem value={ColumnType.TEXT}>Текст</MenuItem>
                            <MenuItem value={ColumnType.NUMBER}>Число</MenuItem>
                            <MenuItem value={ColumnType.DATE}>Дата</MenuItem>
                            <MenuItem value={ColumnType.BOOLEAN}>Да/Нет</MenuItem>
                            <MenuItem value={ColumnType.DROPDOWN}>Выпадающий список</MenuItem>
                          </TextField>
                        )}
                      />
                    </Grid>
                    
                    <Grid size={{ xs: 12 }}>
                      <Controller
                        name="description"
                        control={columnControl}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Описание"
                            fullWidth
                            error={!!columnErrors.description}
                            helperText={columnErrors.description?.message}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              }
                            }}
                          />
                        )}
                      />
                    </Grid>
                    
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Controller
                        name="isRequired"
                        control={columnControl}
                        render={({ field }) => (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={field.value}
                                onChange={field.onChange}
                              />
                            }
                            label="Обязательное поле"
                          />
                        )}
                      />
                    </Grid>
                    
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Controller
                        name="isUnique"
                        control={columnControl}
                        render={({ field }) => (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={field.value}
                                onChange={field.onChange}
                              />
                            }
                            label="Уникальное значение"
                          />
                        )}
                      />
                    </Grid>

                    {/* Dropdown options */}
                    {watchColumnType === ColumnType.DROPDOWN && (
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                          Варианты выбора
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                          <TextField
                            size="small"
                            fullWidth
                            value={newOption}
                            onChange={(e) => setNewOption(e.target.value)}
                            placeholder="Введите вариант и нажмите Enter"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddOption();
                              }
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              }
                            }}
                          />
                          <Button
                            variant="outlined"
                            onClick={handleAddOption}
                            sx={{ minWidth: 44, borderRadius: 2 }}
                          >
                            <AddIcon />
                          </Button>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {dropdownOptions.map((opt, i) => (
                            <Chip
                              key={i}
                              label={opt}
                              onDelete={() => handleRemoveOption(i)}
                              color="primary"
                              variant="outlined"
                              size="small"
                            />
                          ))}
                        </Box>
                        {dropdownOptions.length === 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            Добавьте хотя бы один вариант для выпадающего списка
                          </Typography>
                        )}
                      </Grid>
                    )}

                    {/* ✅ Formula Editor */}
                    <Grid size={{ xs: 12 }}>
                      <FormulaEditor
                        value={columnFormula}
                        onChange={setColumnFormula}
                        availableColumns={availableColumnsForFormula}
                        currentColumnName={watchColumnName}
                        disabled={submitting}
                      />
                    </Grid>
                    
                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button
                          variant="outlined"
                          onClick={handleCloseColumnForm}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                          }}
                        >
                          Отмена
                        </Button>
                        
                        <Button
                          type="submit"
                          variant="contained"
                          disabled={watchColumnType === ColumnType.DROPDOWN && dropdownOptions.length === 0}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                          }}
                        >
                          Добавить
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </form>
              </CardContent>
            </Card>
          </Collapse>
          
          {columns.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <ViewColumnIcon sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.3, mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Нет добавленных колонок
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Нажмите "Добавить колонку" чтобы начать
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {columns.map((column, index) => (
                <ListItem 
                  key={index} 
                  divider
                  sx={{
                    py: 2,
                    borderRadius: 2,
                    mb: 1,
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: 'rgba(245, 87, 108, 0.05)',
                    }
                  }}
                >
                  <Avatar 
                    sx={{ 
                      bgcolor: `${columnTypeIcons[column.type].color}15`,
                      color: columnTypeIcons[column.type].color,
                      mr: 2,
                      width: 40,
                      height: 40,
                    }}
                  >
                    {columnTypeIcons[column.type].icon}
                  </Avatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {column.name}
                        </Typography>
                        {column.isRequired && (
                          <Chip label="Обязательное" size="small" color="error" sx={{ height: 20, fontSize: '0.7rem' }} />
                        )}
                        {column.isUnique && (
                          <Chip label="Уникальное" size="small" color="primary" sx={{ height: 20, fontSize: '0.7rem' }} />
                        )}
                        {column.formula && (
                          <Chip
                            icon={<FunctionsIcon sx={{ fontSize: '0.85rem !important' }} />}
                            label={column.formula.type}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              bgcolor: 'rgba(102, 126, 234, 0.1)',
                              color: '#667eea',
                              '& .MuiChip-icon': { color: '#667eea' },
                            }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {getColumnTypeName(column.type)}
                        </Typography>
                        {column.type === ColumnType.DROPDOWN && column.options?.values?.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                            {column.options.values.map((val: string, i: number) => (
                              <Chip key={i} label={val} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                            ))}
                          </Box>
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => handleMoveColumnUp(index)}
                      disabled={index === 0}
                      size="small"
                      sx={{ mr: 0.5 }}
                    >
                      <ArrowUpIcon />
                    </IconButton>
                    
                    <IconButton
                      edge="end"
                      onClick={() => handleMoveColumnDown(index)}
                      disabled={index === columns.length - 1}
                      size="small"
                      sx={{ mr: 0.5 }}
                    >
                      <ArrowDownIcon />
                    </IconButton>
                    
                    <IconButton
                      edge="end"
                      onClick={() => handleRemoveColumn(index)}
                      size="small"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      </Fade>
      
      {/* Кнопки действий */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={handleCancel}
          disabled={submitting}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
          }}
        >
          Отмена
        </Button>
        
        <Button
          variant="contained"
          onClick={handleTableSubmit(onSubmitTable as SubmitHandler<TableFormData>)}
          disabled={submitting || columns.length === 0}
          startIcon={submitting ? <CircularProgress size={20} /> : <CheckIcon />}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
            },
            '&:disabled': {
              background: 'rgba(0,0,0,0.12)',
            }
          }}
        >
          {submitting ? 'Сохранение...' : 'Создать таблицу'}
        </Button>
      </Box>
    </Box>
  );
}

function getColumnTypeName(type: ColumnType): string {
  switch (type) {
    case ColumnType.TEXT:
      return 'Текст';
    case ColumnType.NUMBER:
      return 'Число';
    case ColumnType.DATE:
      return 'Дата';
    case ColumnType.BOOLEAN:
      return 'Да/Нет';
    case ColumnType.DROPDOWN:
      return 'Выпадающий список';
    case ColumnType.REFERENCE:
      return 'Ссылка';
    default:
      return 'Неизвестный тип';
  }
}